import React from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {InputAdornment} from '@mui/material';
import {NumberField} from '@netx/core-components/NumberField';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {FileSizeType, FileSizeMetric} from '../../constants';
import {getFileSizeSubTypeLabel, getFileSizeTypeLabel} from '../../utils';
import {
	fileSizeRuleEditorClasses,
	getFileSizeRuleEditorUtilityClass,
	FileSizeRuleEditorClasses,
	FILE_SIZE_RULE_EDITOR_CLASS_NAME,
} from './fileSizeRuleEditorClasses';

export type FileSizeRuleEditorOwnerState = WithClasses<
	FileSizeRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: FileSizeRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getFileSizeRuleEditorUtilityClass, classes);
};

const FileSizeRuleEditorRoot = styled('div', {
	name: FILE_SIZE_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
}));

const FileSizeEditorCriteria = styled('div', {
	name: FILE_SIZE_RULE_EDITOR_CLASS_NAME,
	slot: 'Criteria',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const FileSizeEditorValues = styled('div', {
	name: FILE_SIZE_RULE_EDITOR_CLASS_NAME,
	slot: 'Values',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	marginTop: theme.spacing(2),
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

export type FileSizeRuleEditorProps = WithClasses<
	FileSizeRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: FileSizeType;
		elementSubType1Options: FileSizeType[];
		elementSubType2: FileSizeMetric;
		elementSubType2Options: FileSizeMetric[];
		elementValue1: number;
		onSubType1Change: (subType1: FileSizeType) => void;
		onSubType2Change: (subType2: FileSizeMetric) => void;
		onValue1Change: (value: number) => void;
	}
>;

const FileSizeRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementSubType2,
	elementSubType2Options,
	elementValue1,
	validationErrors,
	onSubType1Change,
	onSubType2Change,
	onValue1Change,
}: FileSizeRuleEditorProps) => {
	const ownerState: FileSizeRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<FileSizeRuleEditorRoot className={clsx(classes.root, className)}>
			<FileSizeEditorCriteria>
				<SelectField<FileSizeType, false, true, false>
					data-testid="file-size-rule-sub-type-1"
					disableClearable
					label={SearchSubTypeLabel.MATCH_TYPE}
					name={'rule-sub-type1'}
					options={elementSubType1Options}
					value={elementSubType1}
					getOptionLabel={getFileSizeTypeLabel}
					onChange={(event, value) => onSubType1Change(value)}
				/>

				<SelectField<FileSizeMetric, false, true, false>
					data-testid="file-size-rule-sub-type-2"
					disableClearable
					name={'rule-sub-type-2'}
					label={i18n.t('i18n.unit')}
					options={elementSubType2Options}
					value={elementSubType2}
					getOptionLabel={getFileSizeSubTypeLabel}
					onChange={(event, value) => onSubType2Change(value)}
				/>
			</FileSizeEditorCriteria>

			<FileSizeEditorValues>
				<NumberField
					data-testid="file-size-rule-value-1"
					error={validationErrors?.elementValue1?.message}
					label={i18n.t('i18n.size')}
					name="rule-value-1"
					value={
						!isNaN(elementValue1) && elementValue1.toString() !== ''
							? elementValue1
							: ''
					}
					onChange={(event, value) => onValue1Change(parseInt(value))}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end" disablePointerEvents>
								{getFileSizeSubTypeLabel(elementSubType2)}
							</InputAdornment>
						),
					}}
				/>
			</FileSizeEditorValues>
		</FileSizeRuleEditorRoot>
	);
};

export const FileSizeRuleEditorPrimitive = FileSizeRuleEditorInner;

export const FileSizeRuleEditor = FileSizeRuleEditorPrimitive;

export default FileSizeRuleEditor;

export type FileSizeRuleEditorGenericProps = WithClasses<
	FileSizeRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const FileSizeRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: FileSizeRuleEditorGenericProps) => {
	return (
		<FileSizeRuleEditor
			{...props}
			elementValue1={parseInt(elementValue1.toString())}
		/>
	);
};

export const FileSizeRuleEditorGeneric = FileSizeRuleEditorGenericPrimitive;
